import { ChainId } from '@pancakeswap/chains'
import { useTranslation } from '@pancakeswap/localization'
import { Currency, Token } from '@pancakeswap/sdk'
import { WrappedTokenInfo } from '@pancakeswap/token-lists'
import { AutoColumn, BscScanIcon, Flex, Grid, HelpIcon, Link, Text, useTooltip } from '@pancakeswap/uikit'
import truncateHash from '@pancakeswap/utils/truncateHash'
import { ListLogo } from '@pancakeswap/widgets-internal'
import AccessRisk, { TOKEN_RISK } from '@/components/AccessRisk'
import { ACCESS_TOKEN_SUPPORT_CHAIN_IDS } from '@/components/AccessRisk/config/supportedChains'
import { fetchRiskToken } from '@/components/AccessRisk/utils/fetchTokenRisk'
import { useActiveChainId } from '@/hooks/useActiveChainId'
import { useState } from 'react'
import { useCombinedInactiveList } from '@/state/lists/hooks'
import { useAddUserToken } from '@/state/user/hooks'
import { getBlockExploreLink, getBlockExploreName } from '@/utils'
import { chains } from '@/utils/wagmi'
import { useQuery } from '@tanstack/react-query'
import { AlertCircleIcon, CircleAlertIcon } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Typography } from '@/components/ui/typography'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'

interface ImportProps {
  tokens: Token[]
  handleCurrencySelect?: (currency: Currency) => void
}

function ImportToken({ tokens, handleCurrencySelect }: ImportProps) {
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState(false)
  const addToken = useAddUserToken()

  // use for showing import source on inactive tokens
  const inactiveTokenList = useCombinedInactiveList()

  const { data: hasRiskToken } = useQuery({
    queryKey: ['has-risks', tokens],

    queryFn: async () => {
      const result = await Promise.all(tokens.map((token) => fetchRiskToken(token.address, token.chainId)))
      return result.some((r) => r.riskLevel >= TOKEN_RISK.MEDIUM)
    },

    enabled: Boolean(tokens),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  })

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t('I have read the scanning result, understood the risk and want to proceed with token importing.'),
  )

  return (
    <div className={'flex flex-col gap-6'}>
      <Alert variant={'destructive'}>
        <AlertCircleIcon />
        <AlertDescription>
          {t(
            'Anyone can create tokens on %network% with any name, including creating fake versions of existing tokens and tokens that claim to represent projects that do not have a token.',
            {
              network: chains.find((c) => c.id === chainId)?.name,
            },
          )}
          <b>{t('If you purchase a fraudulent token, you may be exposed to permanent loss of funds.')}</b>
        </AlertDescription>
      </Alert>

      {tokens.map((token) => {
        const list = token.chainId && inactiveTokenList?.[token.chainId]?.[token.address]?.list
        const address = token.address ? `${truncateHash(token.address)}` : null
        return (
          <div className={'flex flex-col gap-2 items-start justify-between'} key={token.address}>
            {list !== undefined ? (
              <Typography className={'flex items-center gap-2'}>
                <>{list.logoURI && <ListLogo logoURI={list.logoURI} size="12px" />}</>
                {t('via')} {list.name}
              </Typography>
            ) : (
              <Typography className={'flex gap-2 items-center text-destructive'} variant={'tip'}>
                <CircleAlertIcon className={'size-4'} />
                {t('Unknown Source')}
              </Typography>
            )}
            <div className={'flex items-center gap-2'}>
              <Typography>{token.name}</Typography>
              <Typography variant={'tip'}>{token.symbol}</Typography>
            </div>
            {!!token.chainId && (
              <div className={'flex gap-2 '}>
                <Typography>{address}</Typography>
                <Link href={getBlockExploreLink(token.address, 'address', token.chainId)} external>
                  (
                  {t('View on %site%', {
                    site: getBlockExploreName(token.chainId),
                  })}
                  {token.chainId === ChainId.BSC && <BscScanIcon color="primary" ml="4px" />})
                </Link>
              </div>
            )}
            {token && chainId && ACCESS_TOKEN_SUPPORT_CHAIN_IDS.includes(chainId) && (
              <Flex mt={['20px', '20px', '0']}>
                <AccessRisk token={token} />
              </Flex>
            )}
          </div>
        )
      })}

      <div className={'flex flex-col gap-4'}>
        <div className={'flex items-center gap-2'}>
          <Checkbox id="confirmed" checked={confirmed} onCheckedChange={() => setConfirmed(!confirmed)} />
          <label htmlFor="confirmed" className="text-sm">
            <Typography>{hasRiskToken ? t('I acknowledge the risk') : t('I understand')}</Typography>
          </label>
          {hasRiskToken && (
            <div ref={targetRef}>
              <HelpIcon color="textSubtle" />
              {tooltipVisible && tooltip}
            </div>
          )}
        </div>
        <Button
          variant="destructive"
          disabled={!confirmed}
          onClick={() => {
            tokens.forEach((token) => {
              const inactiveToken = chainId && inactiveTokenList?.[token.chainId]?.[token.address]
              let tokenToAdd = token
              if (inactiveToken) {
                tokenToAdd = new WrappedTokenInfo({
                  ...token,
                  logoURI: inactiveToken.token.logoURI,
                  name: token.name || inactiveToken.token.name,
                })
              }
              addToken(tokenToAdd)
            })
            if (handleCurrencySelect) {
              handleCurrencySelect(tokens[0])
            }
          }}
          className=".token-dismiss-button"
        >
          {hasRiskToken ? t('Proceed') : t('Import')}
        </Button>
      </div>
    </div>
  )
}

export default ImportToken
